import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  borrarReceta,
  obtenerPerfilTutorLocal,
  obtenerReceta,
  type RecetaDetalleDto,
} from "../api";
import { formatearFecha } from "../formato";

export default function RecetaDetalle() {
  const { recetaID } = useParams();
  const navegar = useNavigate();
  const qc = useQueryClient();

  const [confirmandoBorrar, setConfirmandoBorrar] = useState(false);
  const [copiado, setCopiado] = useState(false);

  const receta = useQuery({
    queryKey: ["receta", recetaID],
    queryFn: () => obtenerReceta(recetaID!),
    enabled: Boolean(recetaID),
  });

  const mutacionBorrar = useMutation({
    mutationFn: () => borrarReceta(recetaID!),
    onSuccess: () => {
      if (receta.data) {
        qc.invalidateQueries({ queryKey: ["recetas", receta.data.mascotaID] });
        navegar(`/mascotas/${receta.data.mascotaID}`);
      } else {
        navegar("/");
      }
    },
  });

  if (receta.isPending) return <p className="muted">Cargando receta médica…</p>;
  if (receta.error) return <p className="err">{receta.error.message}</p>;
  if (!receta.data) return null;

  const r: RecetaDetalleDto = receta.data;

  // Complementar datos del tutor desde storage local si faltan en backend
  const tutorLocal = obtenerPerfilTutorLocal("", r.tutorNombre ?? "Tutor", r.tutorEmail ?? "");
  const tutorNombreFinal = r.tutorNombre || tutorLocal.nombreCompleto || "Tutor responsable";
  const tutorRutFinal = tutorLocal.rut || "—";
  const tutorTelefonoFinal = r.tutorTelefono || tutorLocal.telefono || tutorLocal.telefonoEmergencia || "—";
  const tutorDireccionFinal = [tutorLocal.direccion, tutorLocal.comuna].filter(Boolean).join(", ") || "—";

  // Generar texto para WhatsApp y portapapeles
  const generarTextoReceta = () => {
    let texto = `🐾 *RECETA MÉDICA VETERINARIA*\n`;
    texto += `*Paciente:* ${r.mascotaNombre} (${r.especieNombre}${r.razaNombre ? ` · ${r.razaNombre}` : ""})\n`;
    texto += `*Tutor:* ${tutorNombreFinal}\n`;
    texto += `*Fecha:* ${formatearFecha(r.fechaEmision)}\n`;
    if (r.diagnostico) texto += `*Diagnóstico:* ${r.diagnostico}\n`;
    texto += `\n📋 *PRESCRIPCIÓN (Rp.):*\n`;

    r.items.forEach((item, idx) => {
      texto += `\n${idx + 1}. *${item.medicamentoNombre}* ${item.presentacion ? `(${item.presentacion})` : ""}\n`;
      texto += `   • *Dosis:* ${item.dosis}\n`;
      texto += `   • *Frecuencia:* ${item.frecuencia}\n`;
      if (item.duracion) texto += `   • *Duración:* ${item.duracion}\n`;
      if (item.instrucciones) texto += `   • *Indicación:* ${item.instrucciones}\n`;
    });

    if (r.indicacionesGenerales) {
      texto += `\n📌 *Indicaciones Generales:*\n${r.indicacionesGenerales}\n`;
    }

    if (r.profesionalNombre) {
      texto += `\n👨‍⚕️ *Médico Veterinario:* ${r.profesionalNombre}\n`;
      if (r.profesionalNumeroRegistro) texto += `*Reg. Médico:* ${r.profesionalNumeroRegistro}\n`;
    }

    return texto;
  };

  const copiarTexto = () => {
    navigator.clipboard.writeText(generarTextoReceta());
    setCopiado(true);
    setTimeout(() => setCopiado(false), 3000);
  };

  const enviarWhatsApp = () => {
    const texto = encodeURIComponent(generarTextoReceta());
    const telLimpio = tutorTelefonoFinal.replace(/[^0-9]/g, "");
    if (telLimpio && telLimpio.length >= 8) {
      window.open(`https://wa.me/${telLimpio}?text=${texto}`, "_blank");
    } else {
      window.open(`https://wa.me/?text=${texto}`, "_blank");
    }
  };

  const descargarPDF = () => {
    const tituloAnterior = document.title;
    const fecha = r.fechaEmision || new Date().toISOString().split("T")[0];
    const folio = r.recetaID ? `RP-${r.recetaID.slice(0, 8).toUpperCase()}` : "RP";
    const nombreLimpio = (r.mascotaNombre || "Mascota").trim().replace(/[^a-zA-Z0-9_-]/g, "_");
    document.title = `Receta_${folio}_${nombreLimpio}_${fecha}`;
    window.print();
    setTimeout(() => {
      document.title = tituloAnterior;
    }, 1500);
  };

  return (
    <>
      <div className="no-print">
        <p className="migas">
          <Link to={`/mascotas/${r.mascotaID}`}>← Volver a la ficha de {r.mascotaNombre}</Link>
        </p>

        {/* Barra de Acciones Superior */}
        <div className="receta-acciones-top" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.75rem", marginBottom: "1.5rem" }}>
          <div>
            <h1 style={{ margin: 0 }}>Receta Médica Veterinaria</h1>
            <p className="muted" style={{ margin: 0, fontSize: "0.9rem" }}>
              Emitida el {formatearFecha(r.fechaEmision)} para <strong>{r.mascotaNombre}</strong>
            </p>
          </div>

          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <button
              type="button"
              className="boton-primario"
              onClick={descargarPDF}
              title="Descargar en PDF o imprimir receta médica oficial"
            >
              📄 Descargar en PDF / Imprimir
            </button>
            <button type="button" className="boton" onClick={enviarWhatsApp} title="Enviar por WhatsApp">
              💬 WhatsApp
            </button>
            <button type="button" className="boton" onClick={copiarTexto}>
              {copiado ? "✅ ¡Copiado!" : "📋 Copiar texto"}
            </button>
            <button
              type="button"
              className="boton-peligro"
              onClick={() => setConfirmandoBorrar(true)}
              title="Eliminar esta receta"
            >
              🗑️
            </button>
          </div>
        </div>

        {confirmandoBorrar && (
          <div className="panel zona-riesgo" style={{ marginBottom: "1.5rem" }}>
            <p>¿Estás seguro de que deseas eliminar esta receta médica?</p>
            <div className="acciones">
              <button
                className="boton-peligro"
                onClick={() => mutacionBorrar.mutate()}
                disabled={mutacionBorrar.isPending}
              >
                {mutacionBorrar.isPending ? "Eliminando…" : "Sí, eliminar"}
              </button>
              <button onClick={() => setConfirmandoBorrar(false)}>Cancelar</button>
            </div>
            {mutacionBorrar.error && <p className="err">{mutacionBorrar.error.message}</p>}
          </div>
        )}
      </div>

      {/* DOCUMENTO OFICIAL DE RECETA (Formato Imprimible Rp.) */}
      <div className="documento-receta-imprimible">
        {/* Encabezado Membrete */}
        <header className="receta-header">
          <div className="receta-clinica-info">
            <h2 className="receta-clinica-nombre">
              {r.clinicaNombre || "Centro Médico Veterinario"}
            </h2>
            {r.clinicaDireccion && <p className="receta-clinica-sub">{r.clinicaDireccion}</p>}
            {r.clinicaTelefono && <p className="receta-clinica-sub">Tel: {r.clinicaTelefono}</p>}
          </div>

          <div className="receta-folio-box">
            <span className="receta-tipo-doc">RECETA MÉDICA VETERINARIA</span>
            <span className="receta-folio">Folio: REC-{r.recetaID.slice(0, 8).toUpperCase()}</span>
            <span className="receta-fecha-emision">Fecha: {formatearFecha(r.fechaEmision)}</span>
            <span className="receta-validez">Validez: {r.validezDias} días</span>
          </div>
        </header>

        <hr className="receta-divisor" />

        {/* Ficha del Paciente y Tutor */}
        <section className="receta-identificacion-grid">
          <div className="receta-bloque-paciente">
            <h3 className="receta-seccion-titulo">DATOS DEL PACIENTE</h3>
            <div className="receta-dl">
              <span className="receta-lbl">Paciente:</span>
              <strong className="receta-val">{r.mascotaNombre}</strong>
              <span className="receta-lbl">Especie / Raza:</span>
              <span className="receta-val">{r.especieNombre} {r.razaNombre ? `· ${r.razaNombre}` : ""}</span>
              <span className="receta-lbl">Sexo / Edad:</span>
              <span className="receta-val">{r.sexoNombre || "—"} {r.edadFormateada ? `· ${r.edadFormateada}` : ""}</span>
              <span className="receta-lbl">Peso:</span>
              <span className="receta-val">{r.ultimoPesoKg != null ? `${r.ultimoPesoKg.toFixed(1)} kg` : "—"}</span>
              {r.numeroChip && (
                <>
                  <span className="receta-lbl">Microchip:</span>
                  <span className="receta-val">{r.numeroChip}</span>
                </>
              )}
            </div>
          </div>

          <div className="receta-bloque-tutor">
            <h3 className="receta-seccion-titulo">DATOS DEL TUTOR</h3>
            <div className="receta-dl">
              <span className="receta-lbl">Tutor / Dueño:</span>
              <strong className="receta-val">{tutorNombreFinal}</strong>
              <span className="receta-lbl">RUT / Doc:</span>
              <span className="receta-val">{tutorRutFinal}</span>
              <span className="receta-lbl">Teléfono:</span>
              <span className="receta-val">{tutorTelefonoFinal}</span>
              <span className="receta-lbl">Dirección:</span>
              <span className="receta-val">{tutorDireccionFinal}</span>
            </div>
          </div>
        </section>

        {r.diagnostico && (
          <div className="receta-diagnostico-box">
            <strong>DIAGNÓSTICO CLÍNICO:</strong> {r.diagnostico}
          </div>
        )}

        <hr className="receta-divisor" />

        {/* Cuerpo de la Receta (Rp.) */}
        <section className="receta-cuerpo-rp">
          <div className="receta-rp-simbolo">Rp.</div>

          <div className="receta-lista-farmacos">
            {r.items.map((item, idx) => (
              <div key={item.recetaItemID || idx} className="receta-item-fila">
                <div className="receta-item-numero">{idx + 1}.</div>
                <div className="receta-item-contenido">
                  <div className="receta-farmaco-nombre-fila">
                    <strong className="receta-farmaco-nombre">{item.medicamentoNombre}</strong>
                    {item.presentacion && (
                      <span className="receta-farmaco-presentacion"> — {item.presentacion}</span>
                    )}
                  </div>

                  <div className="receta-farmaco-posologia">
                    <span><strong>Dosis:</strong> {item.dosis}</span>
                    <span> · <strong>Frecuencia:</strong> {item.frecuencia}</span>
                    {item.duracion && <span> · <strong>Duración:</strong> {item.duracion}</span>}
                  </div>

                  {item.instrucciones && (
                    <p className="receta-farmaco-instrucciones">
                      👉 <em>{item.instrucciones}</em>
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Indicaciones Generales y Cuidados */}
        {r.indicacionesGenerales && (
          <section className="receta-indicaciones-box">
            <h3 className="receta-seccion-titulo">INDICACIONES Y CUIDADOS GENERALES</h3>
            <p className="receta-indicaciones-texto">{r.indicacionesGenerales}</p>
          </section>
        )}

        {/* Pie de Firma Profesional */}
        <footer className="receta-footer-firma">
          <div className="receta-firma-caja">
            <div className="receta-linea-firma" />
            <strong className="receta-profesional-nombre">
              {r.profesionalNombre || "Médico Veterinario"}
            </strong>
            <span className="receta-profesional-sub">
              {r.profesionalTitulo || "Médico Veterinario"}
            </span>
            {r.profesionalNumeroRegistro && (
              <span className="receta-profesional-sub">
                Reg. Colegio Médico Vet: {r.profesionalNumeroRegistro}
              </span>
            )}
          </div>
        </footer>
      </div>
    </>
  );
}
